/** Nieuw verbeterpunt / verbeterpunt bekijken en bewerken. Opslaan schrijft naar Airtable (via de engine). */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { verbeterApi } from './verbeterApi';
import { korteDatum, platteTekst, verschil } from './verbeterLogica';
import { overlegApi } from '../Overleg/overlegApi';

const LEEG = {
  onderwerp: '', aanleiding: '', verbeterpunten: '', status: 'Nog bespreken', fase: '', impact: '', urgentie: '',
  type: '', bron: '', hoeGeborgd: '', risico: '', actiepunten: '', preventief: '', correctief: '',
  beoordeling: '', impactbeoordeling: false, projecten: [], submitter: [], mtVerantwoordelijke: [], verantwoordelijke: [],
};

const SELECTS = [
  ['status', 'Status'], ['fase', 'Fase'], ['impact', 'Impact'],
  ['urgentie', 'Urgentie'], ['type', 'Type'], ['bron', 'Bron'],
];
const MAATREGELEN = [
  ['actiepunten', 'Actiepunten (plan)'], ['correctief', 'Corrigerende maatregelen'],
  ['preventief', 'Preventieve maatregelen'], ['risico', 'Risico'],
];

const kop = { fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' };

function PersonenKiezer({ label, value, personen, onChange, disabled }) {
  // Wie niet (meer) actief is maar wel gekoppeld staat, moet zichtbaar blijven.
  const opties = useMemo(() => {
    const ids = new Set(personen.map((p) => p.id));
    return [...personen, ...value.filter((v) => !ids.has(v.id))];
  }, [personen, value]);
  return (
    <Autocomplete multiple size="small" options={opties} value={value} disabled={disabled}
      getOptionLabel={(o) => o.naam || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, v) => onChange(v)} renderInput={(p) => <TextField {...p} label={label} />} />
  );
}

function ProjectKiezer({ value, onChange, disabled }) {
  const [invoer, setInvoer] = useState('');
  const [opties, setOpties] = useState([]);
  useEffect(() => {
    if (invoer.trim().length < 2) { setOpties([]); return undefined; }
    const t = setTimeout(() => { verbeterApi.projecten(invoer).then(setOpties).catch(() => setOpties([])); }, 250);
    return () => clearTimeout(t);
  }, [invoer]);
  return (
    <Autocomplete multiple size="small" options={opties} value={value} disabled={disabled} filterOptions={(x) => x}
      getOptionLabel={(o) => [o.nummer, o.naam].filter(Boolean).join(' · ')} isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v) => setInvoer(v)} onChange={(_, v) => onChange(v)} noOptionsText="Typ een projectnummer of naam"
      renderInput={(p) => <TextField {...p} label="Project" />} />
  );
}

function BesprekenInOverleg({ punt }) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [bezig, setBezig] = useState(false);
  useEffect(() => { if (open && !types.length) overlegApi.types().then((l) => setTypes((l || []).filter((t) => !t.alleen_beheer))).catch(() => setTypes([])); }, [open, types.length]);
  const zet = async () => {
    setBezig(true);
    try {
      const link = `${window.location.origin}/verbeterregister?punt=${punt.id}`;
      await overlegApi.issuePost({
        type_id: Number(typeId),
        titel: `Verbeterpunt${punt.nummer ? ` #${punt.nummer}` : ''}: ${punt.onderwerp}`,
        toelichting: [platteTekst(punt.verbeterpunten), `Verbeterregister: ${link}`].filter(Boolean).join('\n\n'),
        bron_ref: `verbeter:${punt.id}`.slice(0, 64),
      });
      toast.success(`Staat op de issuelijst van ${types.find((t) => String(t.id) === String(typeId))?.naam || 'het overleg'}`);
      setOpen(false);
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };
  if (!open) return <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>Bespreken in overleg</Button>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField select size="small" label="Overleg" value={typeId} onChange={(e) => setTypeId(e.target.value)} sx={{ minWidth: 200 }}>
        {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.naam}</MenuItem>)}
      </TextField>
      <Button size="small" disabled={!typeId || bezig} onClick={zet} sx={{ textTransform: 'none', fontWeight: 700 }}>Op de issuelijst</Button>
    </Stack>
  );
}

export default function VerbeterpuntDialog({ open, punt, opties, personen, alleenLezen, onClose, onOpgeslagen }) {
  const nieuw = !punt;
  const [f, setF] = useState(LEEG);
  const [meer, setMeer] = useState(false);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(punt ? { ...LEEG, ...Object.fromEntries(Object.entries(punt).map(([k, v]) => [k, v ?? LEEG[k] ?? ''])) } : LEEG);
    setMeer(false);
  }, [open, punt]);

  const zet = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const velden = useMemo(() => {
    const { projecten, submitter, mtVerantwoordelijke, verantwoordelijke, ...rest } = f;
    const uit = {};
    for (const k of Object.keys(LEEG)) if (k in rest) uit[k] = rest[k];
    return { ...uit, projecten, submitter, mtVerantwoordelijke, verantwoordelijke };
  }, [f]);

  const opslaan = async () => {
    if (!f.onderwerp.trim()) { toast.error('Geef het verbeterpunt een onderwerp'); return; }
    setBezig(true);
    try {
      if (nieuw) {
        const body = verschil({ ...LEEG, status: null }, velden);
        const { punt: gemaakt } = await verbeterApi.nieuw(body);
        toast.success('Verbeterpunt toegevoegd');
        onOpgeslagen(gemaakt, null);
      } else {
        const body = verschil(punt, velden);
        if (!Object.keys(body).length) { onClose(); return; }
        const { punt: bijgewerkt } = await verbeterApi.wijzig(punt.id, body);
        toast.success('Opgeslagen');
        onOpgeslagen(bijgewerkt, punt);
      }
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const ro = alleenLezen;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {nieuw ? 'Nieuw verbeterpunt' : `Verbeterpunt${punt?.nummer ? ` #${punt.nummer}` : ''}`}
        {!nieuw && punt?.aangemaaktOp && (
          <Typography component="span" sx={{ ml: 1.5, fontSize: 13, color: '#94a3b8', fontWeight: 400 }}>
            {korteDatum(punt.aangemaaktOp)}{punt.submitter?.length ? ` · ingediend door ${punt.submitter.map((s) => s.naam).join(', ')}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Onderwerp" value={f.onderwerp} onChange={(e) => zet('onderwerp')(e.target.value)} disabled={ro} autoFocus={nieuw} fullWidth />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {SELECTS.map(([k, label]) => (
              <TextField key={k} select size="small" label={label} value={f[k] || ''} onChange={(e) => zet(k)(e.target.value)} disabled={ro}>
                {k !== 'status' && <MenuItem value=""><em>—</em></MenuItem>}
                {(opties?.[k] || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </TextField>
            ))}
          </Box>
          <TextField label="Verbeterpunten" value={f.verbeterpunten} onChange={(e) => zet('verbeterpunten')(e.target.value)} disabled={ro} multiline minRows={3} fullWidth />
          <TextField label="Aanleiding" value={f.aanleiding} onChange={(e) => zet('aanleiding')(e.target.value)} disabled={ro} multiline minRows={2} fullWidth />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <PersonenKiezer label="Verantwoordelijke" value={f.verantwoordelijke} personen={personen} onChange={zet('verantwoordelijke')} disabled={ro} />
            <PersonenKiezer label="MT-verantwoordelijke" value={f.mtVerantwoordelijke} personen={personen} onChange={zet('mtVerantwoordelijke')} disabled={ro} />
            <ProjectKiezer value={f.projecten} onChange={zet('projecten')} disabled={ro} />
            {!nieuw && <PersonenKiezer label="Ingediend door" value={f.submitter} personen={personen} onChange={zet('submitter')} disabled={ro} />}
          </Box>
          {!nieuw && (
            <Box>
              <Button size="small" onClick={() => setMeer((v) => !v)} sx={{ textTransform: 'none', px: 0, color: '#475569' }}>
                {meer ? '▾' : '▸'} Maatregelen en borging
              </Button>
              <Collapse in={meer}>
                <Stack spacing={1.5} sx={{ pt: 1 }}>
                  {MAATREGELEN.map(([k, label]) => (
                    <TextField key={k} label={label} value={f[k] || ''} onChange={(e) => zet(k)(e.target.value)} disabled={ro} multiline minRows={2} fullWidth />
                  ))}
                  <Typography sx={kop}>Borging</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
                    <TextField size="small" label="Hoe geborgd?" value={f.hoeGeborgd || ''} onChange={(e) => zet('hoeGeborgd')(e.target.value)} disabled={ro} />
                    <TextField size="small" type="date" label="Beoordeling" InputLabelProps={{ shrink: true }} value={f.beoordeling || ''} onChange={(e) => zet('beoordeling')(e.target.value)} disabled={ro} />
                  </Box>
                  <FormControlLabel disabled={ro} control={<Checkbox size="small" checked={!!f.impactbeoordeling} onChange={(e) => zet('impactbeoordeling')(e.target.checked)} />}
                    label={<Typography sx={{ fontSize: 14 }}>Impactbeoordeling gedaan</Typography>} />
                </Stack>
              </Collapse>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>{!nieuw && <BesprekenInOverleg punt={punt} />}</Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>{ro ? 'Sluiten' : 'Annuleren'}</Button>
          {!ro && <Button variant="contained" disableElevation disabled={bezig} onClick={opslaan} sx={{ textTransform: 'none', fontWeight: 700 }}>{nieuw ? 'Toevoegen' : 'Opslaan'}</Button>}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
